// Phase 45.5.3 (CANVAS-14) — <CanvasSelectField /> canvas-local <Select> field.
//
// Single component covering the three plain-text inputs the canvas previously
// rendered for Status / Engine Type / Fuel Type. It wraps a shadcn <Select> in
// the SAME provenance envelope FieldRow uses (FieldTintWrapper + FormField +
// FormItem + ProvenanceChip + ConflictChooser) so an AI-filled engine_type /
// fuel_type still shows its violet chip and conflict chooser — and editing it
// (changing the Select) flips it to "✓ Edited by you" via markEdited().
//
// D-5 / D-15: imports v1 hooks/constants/types READ-ONLY; lives under canvas/;
// modified no wizard-folder source (V1 era). The authority sort is the shared
// canvas/lib/sourceAuthority helper (lifted from CanvasFormSections, NOT a
// silently-duplicated map).
//
// THREAT MODEL (45.5.3-01 <threat_model>): option labels render as React text
// children (auto-escaped). No value reaches dangerouslySetInnerHTML; the
// onValueChange writes through react-hook-form field.onChange + markEdited only
// — no window.* side channel (mirrors the FieldRow contract).
//
// Typography: v1 control sizing — text-xs FormLabel (D-3 accepts text-2sm on the
// control body). The disallowed 500-weight utility class appears nowhere here.

import { useFormContext } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { humanizeToken } from '@/modules/core/utils/vocabulary';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@marine/ui/select';
import type {
  BoatOption,
} from '@/modules/inventory/constants/boat-options';
import type {
  ProvenanceEntry,
} from '@/modules/inventory/types/boat.types';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import { FieldTintWrapper } from '../FieldTintWrapper';
import { ProvenanceChip } from '../ProvenanceChip';
import { ConflictChooser } from '../ConflictChooser';
import { FieldNotices } from '../CanvasReview';
import { useProvenance } from '../hooks/useProvenance';
import { sortByAuthority } from '../lib/sourceAuthority';

type WizardFieldName = keyof WizardFormData;

interface CanvasSelectFieldProps {
  /** A top-level WizardFormData key (status_id | engine_type | fuel_type). */
  fieldName: WizardFieldName;
  label: string;
  /** Readonly option list — catalog-fed at render (drive_type / fuel_type / mapped statuses). */
  options: readonly BoatOption[];
  placeholder?: string;
  /**
   * When true (Status fetch in flight), render the v1 inline loading spinner
   * placeholder instead of the Select (mirror ListingDetailsStep.tsx:161-167).
   */
  loading?: boolean;
}

export function CanvasSelectField({
  fieldName,
  label,
  options,
  placeholder,
  loading = false,
}: CanvasSelectFieldProps) {
  const { control } = useFormContext<WizardFormData>();
  const { state, clear, selectSource, markEdited } = useProvenance(fieldName);

  return (
    <FieldTintWrapper fieldName={fieldName}>
      <FormField
        control={control}
        name={fieldName as never}
        render={({ field }) => {
          const currentValue = (field.value as string) || '';
          // Display an off-list value (a new slug, or a user-added catalog term
          // not in the static list) via a transient option so the Select shows
          // it. No badge — recording it is internal-only; the user sees a normal
          // selection.
          const displayOptions =
            currentValue && !options.some((o) => o.value === currentValue)
              ? [...options, { value: currentValue, label: humanizeToken(currentValue) }]
              : options;

          return (
            <FormItem>
              <div className="flex items-center justify-between gap-2">
                <FormLabel className="text-xs">{label}</FormLabel>
                <ProvenanceChip
                  fieldName={fieldName}
                  entry={state.entry}
                  onClear={clear}
                  isLowConfidence={state.isLowConfidence}
                />
              </div>
              {state.isConflict && Array.isArray(state.entry) && (
                <ConflictChooser
                  fieldName={fieldName}
                  variant="radio"
                  // Authority order COO > invoice > BOL > web (D-13). Sort the
                  // candidate values so the highest-authority source is first.
                  entries={sortByAuthority(state.entry as ProvenanceEntry[])}
                  selectedIndex={state.pickedIndex}
                  onSelectOne={(entry) => selectSource(entry)}
                />
              )}
              <FieldNotices fieldName={fieldName} />
              <FormControl>
                {loading ? (
                  // v1 inline loading placeholder (ListingDetailsStep.tsx:161-167).
                  <div className="flex items-center h-8.5 px-3 border border-input rounded-md">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      Loading...
                    </span>
                  </div>
                ) : (
                  <Select
                    value={(field.value as string) || ''}
                    onValueChange={(v) => {
                      field.onChange(v);
                      // Phase 45.5 (D-13 / RESEARCH Pitfall) — editing an
                      // AI-filled Select is the accept gesture: flip it to
                      // user-entered so the violet "AI" chip becomes
                      // "✓ Edited by you" and the value survives a re-extract.
                      // No-op for an empty/manual field; idempotent once edited.
                      markEdited();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {displayOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </FieldTintWrapper>
  );
}
